import { useCallback } from 'react'
import { v4 as uuid4 } from 'uuid'

import { IBody } from '@makyoapp/makyo-sdk/models/AppBuilder'

import { IDB } from '@/core/db'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { DESIGN_BLOCK_MAP, DesignBlockConfig, DesignBlockKey } from '@/shared/builder/design-blocks/constants'

interface SourceResult {
  tableKey: string
  tableKey2: string
  imageField: any
  userEmail: any
  userPhoto: any
  userName: any
  userTableKey: string
  title: any
  subtitle: any
  media?: any
  imageField2: any
  title2: any
  subtitle2: any
  icon2: any
  icon: any
  id: any
  number: any
}

const getFirstTableImageColumns = (db: IDB | undefined): SourceResult => {
  const emptyResult = {
    tableKey: '',
    tableKey2: '',
    userTableKey: '',
    imageField: {},
    userEmail: {},
    userPhoto: {},
    userName: {},
    title: {},
    subtitle: {},
    imageField2: {},
    title2: {},
    subtitle2: {},
    icon2: {},
    icon: {},
    id: {},
    number: {},
  }

  if (!db || [db.tablesMap].length === 0) return emptyResult

  const getTableKeyWithIcon = () => {
    for (const tableKey of db.tablesMap.keys()) {
      const tableEntry = db.tablesMap.get(tableKey)
      const columns = tableEntry?.settings?._Columns
      const iconColumn = columns?.find((column) => column._Type === 'icon')
      if (iconColumn) return tableKey
    }

    const firstTableKey = db.tablesMap.keys().next().value
    return firstTableKey || ''
  }

  const tableKey = getTableKeyWithIcon()
  const firstTableEntry = db.tablesMap.get(tableKey)

  const sample2Table = Array.from(db.tablesMap.values()).find((table) => table.settings._Name === 'Sample 2')
  const sample2Columns = sample2Table?.settings?._Columns
  const imageField2 = sample2Columns?.find((column) => column._Type === 'image')
  const title2 = sample2Columns?.find((column) => column._Type === 'text')
  const firstTextIndex2 = sample2Columns?.findIndex((column) => column._Type === 'text')
  const subtitle2 =
    firstTextIndex2 && sample2Columns?.slice(firstTextIndex2 + 1).find((column) => column._Type === 'text')
  const tableKey2 = sample2Table?.settings._id ?? ''
  const icon2 = sample2Columns?.find((column) => column._Type === 'icon')

  const usersTable = Array.from(db.tablesMap.values()).find((table) => table.settings._Name === 'Users')

  const usersColumns = usersTable?.settings._Columns
  const userEmail = usersColumns?.find((column) => column._Name === 'Email')
  const userPhoto = usersColumns?.find((column) => column._Name === 'Photo')
  const userName = usersColumns?.find((column) => column._Name === 'Full name')

  const _Columns = firstTableEntry?.settings?._Columns

  if (_Columns?.length === 0 || !Array.isArray(_Columns)) return emptyResult

  const imageField = _Columns.find((column) => column._Type === 'image')
  const title = _Columns.find((column) => column._Type === 'text')
  const firstTextIndex = _Columns.findIndex((column) => column._Type === 'text')
  const subtitle = _Columns.slice(firstTextIndex + 1).find((column) => column._Type === 'text')
  const icon = _Columns.find((column) => column._Type === 'icon')
  const number = _Columns.find((column) => column._Type === 'number')
  const id = _Columns.find((column) => column._Name === 'ID')

  return {
    tableKey,
    tableKey2,
    imageField,
    userEmail,
    userPhoto,
    userName,
    userTableKey: usersTable?.settings._id ?? '',
    title,
    subtitle,
    imageField2,
    title2,
    subtitle2,
    icon2,
    icon,
    id,
    number,
  }
}

const generateLayoutWithSettings = (
  layoutInput: IBody[] | { body: IBody[] },
  settingsData: Record<string, any>,
  dbClient: IDB | undefined
) => {
  const idMapping = new Map()

  const { title, subtitle, imageField, userEmail, userPhoto, userName, userTableKey, icon, id, number } =
    getFirstTableImageColumns(dbClient)

  const generateAndMapId = (oldId: string): string => {
    if (!idMapping.has(oldId)) {
      idMapping.set(oldId, uuid4())
    }
    return idMapping.get(oldId) as string
  }

  const updateLayoutIds = (item: any) => {
    if (!item) return null

    const newItem = { ...item }
    if (newItem._id) newItem._id = generateAndMapId(newItem._id)

    if (Array.isArray(newItem._Body)) {
      newItem._Body = newItem._Body.map((child: any) => updateLayoutIds(child))
    }

    return newItem
  }

  const updateTextDataValue = (
    updatedSetting: Record<string, any>,
    setting: Record<string, any>,
    key: string,
    config: {
      userTableKey: string
      userEmail?: any
      userName?: any
      userPhoto?: any
      title?: any
      subtitle?: any
    }
  ) => {
    const transformMap = {
      'data-user-source': { id: 'data-user-source', col: config.userTableKey },
      'data-user-email': { col: config.userEmail?._id },
      'data-user-name': { col: config.userName?._id },
      'data-user-image': { col: config.userPhoto?._id },
      'card-data-title': { col: config.title?._id },
      'card-data-subtitle': { col: config.subtitle?._id },
    }

    if (key.includes('card-data-title')) {
      updatedSetting[key] = { ...setting[key], col: config.title?._id }
    } else if (key.includes('card-data-subtitle')) {
      updatedSetting[key] = { ...setting[key], col: config.subtitle?._id }
    } else if (key.includes('data-user-email')) {
      updatedSetting[key] = { ...setting[key], col: config.userEmail?._id }
    } else if (key.includes('data-user-name')) {
      updatedSetting[key] = { ...setting[key], col: config.userName?._id }
    } else if (key.includes('data-user-image')) {
      updatedSetting[key] = { ...setting[key], col: config.userPhoto?._id }
    } else {
      updatedSetting[key] = setting[key].map((item: { children: any[] }) => {
        if (!item.children) return { ...item }

        return {
          ...item,
          children: item.children.map((child) => {
            const newChild = { ...child }

            const matchingTransform = Object.entries(transformMap).find(
              ([key]) => child?.id?.includes(key) || child.col?.includes(key)
            )

            if (matchingTransform) {
              Object.assign(newChild, matchingTransform[1])
            }

            return newChild
          }),
        }
      })
    }
  }

  const updateSettingsIds = (settings: Record<string, any>) => {
    const newSettings: Record<string, any> = {}
    Object.keys(settings).forEach((oldId) => {
      const setting = settings[oldId]
      const updatedSetting: Record<string, any> = {}
      Object.keys(setting).forEach((key) => {
        const value = setting[key]
        const isSource2Key = value === 'source2'

        if (key.includes('source')) {
          const { tableKey, tableKey2, imageField2, title2, subtitle2 } = getFirstTableImageColumns(dbClient)
          if (isSource2Key) {
            updatedSetting[key] = tableKey2
            updatedSetting['card-data-media'] = imageField2?._id
            updatedSetting['card-data-title'] = title2?._id
            updatedSetting['card-data-subtitle'] = subtitle2?._id
            updatedSetting['card-data-sort-by'] = subtitle2?._id
            updatedSetting['list-data-title'] = title2?._id
            updatedSetting['list-data-supporting-text'] = subtitle2?._id
          } else {
            updatedSetting[key] = tableKey
            updatedSetting['list-data-title'] = title?._id
            updatedSetting['list-data-supporting-text'] = subtitle?._id
          }
        } else if (key.includes('list-data-title')) {
          updatedSetting[key] = title?._id
        } else if (key.includes('list-data-supporting-text')) {
          updatedSetting[key] = subtitle?._id
        } else if (key.includes('data-image')) {
          updatedSetting[key] = imageField?._id
        } else if (key.includes('text-data-value')) {
          updateTextDataValue(updatedSetting, setting, key, {
            userTableKey,
            userEmail,
            userName,
            userPhoto,
            title,
            subtitle,
          })
        } else if (key.includes('card-data-media')) {
          if (value) updatedSetting[key] = value === 'image' ? imageField?._id : icon?._id
        } else if (key.includes('card-data-title')) {
          updatedSetting[key] = value ? title?._id : ''
        } else if (key.includes('card-data-subtitle')) {
          updatedSetting[key] = value ? subtitle?._id : ''
        } else if (key.includes('image-data-src')) {
          updatedSetting[key] = { ...setting[key], col: userPhoto?._id, id: uuid4() }
        } else if (key.includes('list-data-media')) {
          if (value) updatedSetting[key] = value === 'image' ? imageField?._id : icon?._id
        } else if (key.includes('card-data-thumbnail')) {
          updatedSetting[key] = value ? imageField?._id : ''
        } else if (key.includes('gallery-data-image')) {
          if (value) updatedSetting[key] = value === 'image' ? imageField?._id : icon?._id
        } else if (key.includes('gallery-data-caption')) {
          updatedSetting[key] = value ? title?._id : ''
        } else if (key.includes('gallery-data-tag')) {
          updatedSetting[key] = value ? subtitle?._id : ''
        } else if (key.includes('bar-chart-data-x-axis-column')) {
          updatedSetting[key] = value ? id?._id : ''
        } else if (key.includes('bar-chart-data-value-columns')) {
          updatedSetting[key] = value ? [number?._id] : []
        } else if (key.includes('pie-chart-data-label-column')) {
          updatedSetting[key] = value ? id?._id : ''
        } else if (key.includes('pie-chart-data-value-column')) {
          updatedSetting[key] = value ? [number?._id] : []
        } else {
          updatedSetting[key] = setting[key]
        }
      })

      const newId = idMapping.get(oldId)
      if (newId) {
        newSettings[newId] = updatedSetting
      }
    })

    return newSettings
  }

  const layoutData = Array.isArray(layoutInput) ? layoutInput : layoutInput.body

  const updatedLayout = layoutData.map((item) => updateLayoutIds(item))

  const updatedSettings = updateSettingsIds(settingsData)

  const result = {
    layout: updatedLayout,
    settings: updatedSettings,
    idMapping,
  }

  if (!Array.isArray(layoutInput)) {
    return {
      ...result,
      body: result.layout,
    }
  }

  return result
}

const designBlockCache = new Map<DesignBlockKey, DesignBlockConfig>()

export const generateAutoDesignBlocks = async (_elmId: string, compId: string, dbClient: IDB | undefined) => {
  let bodyData: IBody[] = []
  let bodySettingsData = {}

  const designBlockPath = DESIGN_BLOCK_MAP[compId as DesignBlockKey]

  if (designBlockPath) {
    // check cache first
    const cachedData = designBlockCache.get(compId as DesignBlockKey)
    if (cachedData) {
      bodyData = cachedData.body
      bodySettingsData = cachedData.bodySettings
    } else {
      // fetch design block json from public folder
      const data = await fetch(designBlockPath)
      if (data.ok) {
        const json = (await data.json()) as DesignBlockConfig
        bodyData = json.body
        bodySettingsData = json.bodySettings

        // store in cache
        designBlockCache.set(compId as DesignBlockKey, {
          body: bodyData,
          bodySettings: bodySettingsData,
        })
      }
    }
  }

  const { layout, settings } = generateLayoutWithSettings(bodyData, bodySettingsData, dbClient)

  return {
    children: layout,
    childElSettings: settings,
  }
}

export const useDesignBlocksGenerator = () => {
  const { dbClient } = useDbClientContext()

  const generateDesignBlocks = useCallback(
    (elmId: string, compId: string) => generateAutoDesignBlocks(elmId, compId, dbClient),
    [dbClient]
  )

  return { generateDesignBlocks }
}
